import { AxiosResponse } from 'axios';
import { takeLatest, call, put, all, select } from 'redux-saga/effects';
import { PaymentLinkInfo, CompletePaymentLinkConfirmation, SendVerificationCodeResponse } from '../../api/types';
import { SimplyTestApi } from '../../api/simplytest';
import { selectConfig } from '../../shared/redux/slice';
import { IConfiguration } from '../../shared/types';
import { extractErrorMessage } from '../../shared/utils';
import patientPaymentSlice, { loadPaymentLink, completePaymentLink, sendVerificationCode, verifyCode } from './slice';

// sessionStorage (not localStorage) is deliberate - it's scoped to this one tab/window, so a
// patient opening the same emailed link in a new window or an incognito profile has none of it
// and gets sent through code verification again, rather than riding along on another window's
// already-verified session.
const sessionStorageKey = (token: string) => `paylink_session_${token}`;
const getStoredSessionToken = (token: string): string | undefined => sessionStorage.getItem(sessionStorageKey(token)) ?? undefined;
const storeSessionToken = (token: string, sessionToken: string) => sessionStorage.setItem(sessionStorageKey(token), sessionToken);

// Public/unauthenticated feature - reached via an emailed one-time token link (Pre-Buy Flow
// 2b-i), so unlike every other saga in this app there's no idToken/unauthorized() handling here.
export abstract class PatientPaymentSagas {
    public static* run(){
        try {
            yield all([
                takeLatest(loadPaymentLink, PatientPaymentSagas.loadPaymentLink),
                takeLatest(completePaymentLink, PatientPaymentSagas.completePaymentLink),
                takeLatest(sendVerificationCode, PatientPaymentSagas.sendVerificationCode),
                takeLatest(verifyCode, PatientPaymentSagas.verifyCode),
            ]);
        }
        catch(e: any){
            console.log(`[PatientPaymentSagas:run] the following exception has occurred`, e);
        }
    }

    private static* loadPaymentLink(action: ReturnType<typeof loadPaymentLink>){
        try {
            yield put(patientPaymentSlice.actions.setLoadState('BUSY'));
            yield put(patientPaymentSlice.actions.setErrorMessage(undefined));
            const config: IConfiguration = yield select(selectConfig);
            const token = action.payload;
            const session = getStoredSessionToken(token);

            var client = new SimplyTestApi(config.apiBaseUrl);
            const response: AxiosResponse<PaymentLinkInfo> = yield call(
                { context: client, fn: client.getPaymentLink },
                token, session,
            );

            if(response.status !== 200){
                const message = extractErrorMessage(response.data, 'This payment link could not be found.');
                yield put(patientPaymentSlice.actions.setErrorMessage(message));
                yield put(patientPaymentSlice.actions.setLoadState('ERROR'));
                return;
            }

            yield put(patientPaymentSlice.actions.setLinkInfo(response.data));
            yield put(patientPaymentSlice.actions.setLoadState('SUCCESS'));
        }
        catch(e: any){
            yield put(patientPaymentSlice.actions.setErrorMessage('Something went wrong loading this payment link. Please try again in a few minutes.'));
            yield put(patientPaymentSlice.actions.setLoadState('ERROR'));
            console.log(`[PatientPaymentSagas:loadPaymentLink] the following exception has occurred`, e);
        }
    }

    private static* completePaymentLink(action: ReturnType<typeof completePaymentLink>){
        try {
            yield put(patientPaymentSlice.actions.setCompleteState('BUSY'));
            yield put(patientPaymentSlice.actions.setErrorMessage(undefined));
            const config: IConfiguration = yield select(selectConfig);
            const { token, paymentIntentId } = action.payload;
            const sessionToken = getStoredSessionToken(token);

            var client = new SimplyTestApi(config.apiBaseUrl);
            const response: AxiosResponse<CompletePaymentLinkConfirmation> = yield call(
                { context: client, fn: client.completePaymentLink },
                token, { paymentIntentId, sessionToken },
            );

            if(response.status !== 200){
                const message = extractErrorMessage(response.data, 'Error completing payment. Please try again.');
                yield put(patientPaymentSlice.actions.setErrorMessage(message));
                yield put(patientPaymentSlice.actions.setCompleteState('ERROR'));
                return;
            }

            yield put(patientPaymentSlice.actions.setConfirmation(response.data));
            yield put(patientPaymentSlice.actions.setCompleteState('SUCCESS'));
        }
        catch(e: any){
            yield put(patientPaymentSlice.actions.setErrorMessage('Error completing payment. Please try again in a few minutes.'));
            yield put(patientPaymentSlice.actions.setCompleteState('ERROR'));
            console.log(`[PatientPaymentSagas:completePaymentLink] the following exception has occurred`, e);
        }
    }

    private static* sendVerificationCode(action: ReturnType<typeof sendVerificationCode>){
        try {
            yield put(patientPaymentSlice.actions.setCodeSendState('BUSY'));
            yield put(patientPaymentSlice.actions.setCodeErrorMessage(undefined));
            const config: IConfiguration = yield select(selectConfig);
            const token = action.payload;

            var client = new SimplyTestApi(config.apiBaseUrl);
            const response: AxiosResponse<SendVerificationCodeResponse> = yield call(
                { context: client, fn: client.sendPaymentLinkVerificationCode },
                token,
            );

            if(response.status !== 200){
                const message = extractErrorMessage(response.data, 'Unable to send a verification code. Please try again.');
                yield put(patientPaymentSlice.actions.setCodeErrorMessage(message));
                yield put(patientPaymentSlice.actions.setCodeSendState('ERROR'));
                return;
            }

            yield put(patientPaymentSlice.actions.setMaskedEmail(response.data.maskedEmail));
            yield put(patientPaymentSlice.actions.setCodeResendCooldownSeconds(response.data.cooldownSeconds));
            yield put(patientPaymentSlice.actions.setCodeSendState('SUCCESS'));
        }
        catch(e: any){
            const message = extractErrorMessage(e?.response?.data, 'Unable to send a verification code. Please try again in a few minutes.');
            yield put(patientPaymentSlice.actions.setCodeErrorMessage(message));
            yield put(patientPaymentSlice.actions.setCodeSendState('ERROR'));
            console.log(`[PatientPaymentSagas:sendVerificationCode] the following exception has occurred`, e);
        }
    }

    private static* verifyCode(action: ReturnType<typeof verifyCode>){
        try {
            yield put(patientPaymentSlice.actions.setCodeVerifyState('BUSY'));
            yield put(patientPaymentSlice.actions.setCodeErrorMessage(undefined));
            const config: IConfiguration = yield select(selectConfig);
            const { token, code } = action.payload;

            var client = new SimplyTestApi(config.apiBaseUrl);
            const response: AxiosResponse<PaymentLinkInfo> = yield call(
                { context: client, fn: client.verifyPaymentLinkCode },
                token, { code },
            );

            if(response.status !== 200){
                const message = extractErrorMessage(response.data, 'That code isn\'t correct. Please try again.');
                yield put(patientPaymentSlice.actions.setCodeErrorMessage(message));
                yield put(patientPaymentSlice.actions.setCodeVerifyState('ERROR'));
                return;
            }

            if (response.data.sessionToken) storeSessionToken(token, response.data.sessionToken);
            yield put(patientPaymentSlice.actions.setLinkInfo(response.data));
            yield put(patientPaymentSlice.actions.setCodeVerifyState('SUCCESS'));
        }
        catch(e: any){
            const message = extractErrorMessage(e?.response?.data, 'That code isn\'t correct. Please try again.');
            yield put(patientPaymentSlice.actions.setCodeErrorMessage(message));
            yield put(patientPaymentSlice.actions.setCodeVerifyState('ERROR'));
            console.log(`[PatientPaymentSagas:verifyCode] the following exception has occurred`, e);
        }
    }
}
